import React, { useContext, useEffect, useRef, useState } from 'react';

import type { TListingSellerInfo } from 'types/TListing';
import type { TSellerProfileClient } from 'types/TSellerProfile';

import EnvironmentContext from '../../../contexts/EnvironmentContext';
import getSellerProfile from '../../../repositories/getSellerProfile';

import SellerInfoSkeleton from './components/SellerInfoSkeleton';

import Classes from './SellerInfoExpanded.scss';

let SellerHeader;
let SellerTrustIndicators;
let SellerSalesRepresentatives;

type TSellerInfoExpandedProps = {
  seller: TListingSellerInfo;
  isCarAd: boolean;
};

const SellerInfoExpanded = ({ seller, isCarAd }: TSellerInfoExpandedProps) => {
  const { userDetails: { isAdOwner = false } = {} } = useContext(EnvironmentContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSalesRepresentativesLoaded, setIsSalesRepresentativesLoaded] = useState(false);
  const [profile, setProfile] = useState<TSellerProfileClient>({});
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const {
    id,
    name,
    activeSince,
    pageUrl,
    isSaved,
    sellerType,
    showDealerLegalDisclaimer,
    showProfilePicture,
    showSellerResponseRate,
    showSalesRepresentatives,
    showVerifications,
  } = seller;

  // SMB verified does not show for cars, as automotive is not included in the SMB bundles
  // refer to BNL-7414
  const isSmbVerified = !isCarAd && Boolean(profile.smbVerified);
  const isTrader = sellerType === 'TRADER';

  useEffect(() => {
    const elementRef = sectionRef.current as Element;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            (async () => {
              try {
                const [headerChunk, indicatorsChunk, fetchedProfile] = await Promise.all([
                  import(/* webpackChunkName: "SellerHeader" */ './components/SellerHeader'),
                  import(
                    /* webpackChunkName: "SellerTrustIndicators" */ './components/SellerTrustIndicators/SellerTrustIndicators'
                  ),
                  getSellerProfile({
                    sellerId: seller.id,
                    requestExternalReviews: seller.requestExternalReviews,
                  }),
                ]);
                SellerHeader = headerChunk.default;
                SellerTrustIndicators = indicatorsChunk.default;

                setIsLoaded(true);
                setProfile(fetchedProfile);
              } catch (err) {}
            })();
          }
        });
      },
      {
        rootMargin: '-136px 0px 0px 0px',
      },
    );

    if (elementRef && !isLoaded) {
      observer.observe(elementRef);
    }

    return () => {
      if (elementRef) {
        observer.disconnect();
      }
    };
  }, [isLoaded, seller.id, seller.requestExternalReviews]);

  useEffect(() => {
    if (!isSalesRepresentativesLoaded && profile.salesRepresentatives?.length) {
      const loadComponent = async () => {
        try {
          SellerSalesRepresentatives = (
            await import(/* webpackChunkName: "SellerSalesRepresentatives" */ './components/SellerSalesRepresentatives')
          ).default;
          setIsSalesRepresentativesLoaded(true);
        } catch (err) {}
      };

      loadComponent();
    }
  }, [isSalesRepresentativesLoaded, profile.salesRepresentatives?.length]);

  return (
    <section className={`block-wrapper-s ${Classes.root}`} ref={sectionRef}>
      {isLoaded ? (
        <>
          <SellerHeader
            id={id}
            name={name}
            activeSince={activeSince}
            pageUrl={pageUrl}
            isSaved={isSaved}
            isTrader={isTrader}
            isAdOwner={isAdOwner}
            isSmbVerified={isSmbVerified}
            showBackgroundPicture={showProfilePicture}
            {...profile.profilePictures}
          />

          <div className={Classes.contentWrapper}>
            <SellerTrustIndicators
              pageUrl={pageUrl}
              profile={profile}
              sellerId={id}
              sellerName={name}
              sellerType={sellerType}
              showDealerLegalDisclaimer={showDealerLegalDisclaimer}
              showProfilePicture={showProfilePicture}
              showSellerResponseRate={showSellerResponseRate}
              showVerifications={showVerifications}
            />

            {showSalesRepresentatives && isSalesRepresentativesLoaded && (
              <SellerSalesRepresentatives salesRepresentatives={profile.salesRepresentatives} />
            )}
          </div>
        </>
      ) : (
        <SellerInfoSkeleton />
      )}
    </section>
  );
};

export default SellerInfoExpanded;
